<template>
  <v-container fluid>
    <ValidationObserver ref="customerForm" v-slot="{ invalid, passes }">
      <v-form rel="customerForm" @submit.prevent="passes(save)">
        <v-skeleton-loader :loading="customerLoading" :types="skeleton" type="article" class="mb-6">
          <v-card class="elevation-1 mb-6 rounded-lg">
            <v-card-title class="pa-7"
              >Zákazník
              <v-spacer />
              <UserRole v-if="customer.user" :role="customer.user.role" class="mr-1" />
              <UserStatus v-if="customer.user" :status="customer.user.status" />
            </v-card-title>
            <v-card-text class="pl-7 pr-7 pt-7">
              <v-row class="mb-6">
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <div class="request-label">ID</div>
                  <div class="request-value mt-2">
                    <router-link
                      :to="{
                        name: 'customers.detail',
                        params: {
                          id: customer.id,
                        },
                      }"
                    >
                      {{ customer.id }}
                    </router-link>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VAutocompleteWithValidation
                    v-model.trim="customer.genderName"
                    label="Oslovení"
                    outlined
                    dense
                    background-color="white"
                    :items="genders"
                    item-text="text"
                    item-value="name"
                    :loading="gendersLoading"
                    :clearable="true"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model="customer.name"
                    rules="required"
                    outlined
                    dense
                    label="Jméno"
                    data-cy="name"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model="customer.surname"
                    rules="required"
                    outlined
                    dense
                    label="Příjmení"
                    data-cy="surname"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model="customer.titleBefore"
                    outlined
                    dense
                    label="Titul před"
                    data-cy="titleBefore"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model="customer.titleAfter"
                    outlined
                    dense
                    label="Titul za"
                    data-cy="titleAfter"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VDatePickerEditableWithValidation
                    v-model="customer.birthday"
                    date-format="dd.MM.yyyy"
                    outlined
                    dense
                    label="Datum narození"
                    data-cy="phone"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model="customer.phone"
                    rules="required|phone"
                    outlined
                    dense
                    label="Telefon (ve formátu +420123456789)"
                    data-cy="phone"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model="customer.email"
                    rules="email"
                    outlined
                    dense
                    label="E-mail"
                    data-cy="email"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model.trim="customer.bankAccountNumber"
                    label="Číslo bankovního účtu"
                    type="number"
                    outlined
                    dense
                    background-color="white"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model.trim="customer.bankCode"
                    label="Kód banky"
                    type="number"
                    outlined
                    dense
                    background-color="white"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4" class="py-0 px-5">
                  <VTextFieldWithValidation
                    v-model.trim="customer.sipoNumber"
                    label="Číslo SIPO"
                    type="number"
                    outlined
                    dense
                    background-color="white"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="pa-7">
              <v-spacer />
              <v-btn
                type="submit"
                height="40px"
                min-width="130px"
                color="primary"
                depressed
                :loading="saving"
              >
                Sloučit na tohoto zákazníka
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-skeleton-loader>
        <v-row>
          <v-col cols="12" lg="6">
            <v-skeleton-loader
              :loading="customerLoading"
              :types="skeleton"
              type="article"
              class="mb-6"
            >
              <v-card class="elevation-1 mb-6 rounded-lg">
                <v-card-title class="pa-7"> Adresa </v-card-title>
                <v-card-text class="pl-7 pr-7 pt-7">
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.address.street"
                        outlined
                        dense
                        label="Ulice"
                        data-cy="street"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.address.houseNumber2"
                        type="number"
                        outlined
                        dense
                        label="Č.P."
                        data-cy="houseNumber2"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.address.houseNumber"
                        type="number"
                        outlined
                        dense
                        label="Č.O."
                        data-cy="houseNumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.address.city"
                        outlined
                        dense
                        label="Město"
                        data-cy="city"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.address.district"
                        outlined
                        dense
                        label="Místní část"
                        data-cy="district"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.address.postalCode"
                        outlined
                        dense
                        label="PSČ"
                        type="number"
                        data-cy="postalCode"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions />
              </v-card>
            </v-skeleton-loader>
          </v-col>
          <v-col cols="12" lg="6">
            <v-skeleton-loader
              :loading="customerLoading"
              :types="skeleton"
              type="article"
              class="mb-6"
            >
              <v-card class="elevation-1 mb-6 rounded-lg">
                <v-card-title class="pa-7"> Kontaktní adresa </v-card-title>
                <v-card-text class="pl-7 pr-7 pt-7">
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.contactAddress.street"
                        outlined
                        dense
                        label="Ulice"
                        data-cy="street"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.contactAddress.houseNumber2"
                        type="number"
                        outlined
                        dense
                        label="Č.P."
                        data-cy="houseNumber2"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.contactAddress.houseNumber"
                        type="number"
                        outlined
                        dense
                        label="Č.O."
                        data-cy="houseNumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.contactAddress.city"
                        outlined
                        dense
                        label="Město"
                        data-cy="city"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.contactAddress.district"
                        outlined
                        dense
                        label="Místní část"
                        data-cy="district"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" class="py-0 px-5">
                      <VTextFieldWithValidation
                        v-model="customer.contactAddress.postalCode"
                        outlined
                        dense
                        label="PSČ"
                        type="number"
                        data-cy="postalCode"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions />
              </v-card>
            </v-skeleton-loader>
          </v-col>
        </v-row>
        <v-divider class="mb-6 ml-3 mr-3" />
        <v-skeleton-loader
          :loading="customersLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
        >
          <v-row>
            <v-col v-for="item in customers" :key="item.id" cols="12">
              <v-card class="elevation-1 rounded-lg">
                <v-card-title class="pa-7">
                  Zákazník
                  <v-spacer></v-spacer>
                  <UserRole v-if="item.user" :role="item.user.role" class="mr-1" />
                  <UserStatus v-if="item.user" :status="item.user.status" />
                </v-card-title>
                <v-card-text class="pa-7 pt-7">
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">ID</div>
                          <div class="request-value mt-2">
                            <router-link
                              :to="{
                                name: 'customers.detail',
                                params: {
                                  id: item.id,
                                },
                              }"
                            >
                              {{ item.id }}
                            </router-link>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Oslovení</div>
                          <div class="request-value mt-2">
                            {{ item.gender ? item.gender.text : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Jméno</div>
                          <div class="request-value mt-2">
                            {{ item.fullName ? item.fullName : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Titul před</div>
                          <div class="request-value mt-2">
                            {{ item.titleBefore ? item.titleBefore : '-' }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">Titul za</div>
                          <div class="request-value mt-2">
                            {{ item.titleAfter ? item.titleAfter : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Datum narození</div>
                          <div class="request-value mt-2">
                            {{ item.birthday ? $dayjs(item.birthday).format('D.M.YYYY') : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Telefon</div>
                          <div class="request-value mt-2">
                            {{ item.phone ? formatPhoneNumber(item.phone) : '-' }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">E-mail</div>
                          <div class="request-value mt-2">
                            {{ item.email ? item.email : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Číslo bankovního účtu</div>
                          <div class="request-value mt-2">
                            {{
                              item.bankAccountNumber
                                ? item.bankAccountNumber + '/' + item.bankCode
                                : '-'
                            }}
                          </div>
                        </v-col>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Číslo SIPO</div>
                          <div class="request-value mt-2">
                            {{ item.sipoNumber ? item.sipoNumber : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-divider class="mb-10 mt-3 mr-3 ml-3"></v-divider>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <v-row class="pb-6">
                        <v-col cols="12" class="py-0">
                          <v-card-title class="py-0 px-0">Adresa</v-card-title>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" class="py-0">
                          <div class="request-label">Ulice</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.address.street ? item.address.street : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Č.P.</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.address.houseNumber2 ? item.address.houseNumber2 : '-' }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">Č.O.</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.address.houseNumber ? item.address.houseNumber : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Město</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.address.city ? item.address.city : '-' }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">Místní část</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.address.district ? item.address.district : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">PSČ</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.address.postalCode ? item.address.postalCode : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="6">
                      <v-row class="pb-6">
                        <v-col cols="12" class="py-0">
                          <v-card-title class="py-0 px-0">Kontaktní adresa</v-card-title>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" class="py-0">
                          <div class="request-label">Ulice</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.contactAddress.street ? item.contactAddress.street : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Č.P.</div>
                          <div class="request-value mt-2 mb-4">
                            {{
                              item.contactAddress.houseNumber2
                                ? item.contactAddress.houseNumber2
                                : '-'
                            }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">Č.O.</div>
                          <div class="request-value mt-2 mb-4">
                            {{
                              item.contactAddress.houseNumber
                                ? item.contactAddress.houseNumber
                                : '-'
                            }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">Město</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.contactAddress.city ? item.contactAddress.city : '-' }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">Místní část</div>
                          <div class="request-value mt-2 mb-4">
                            {{ item.contactAddress.district ? item.contactAddress.district : '-' }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="py-0">
                          <div class="request-label">PSČ</div>
                          <div class="request-value mt-2 mb-4">
                            {{
                              item.contactAddress.postalCode ? item.contactAddress.postalCode : '-'
                            }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions />
              </v-card>
            </v-col>
          </v-row>
        </v-skeleton-loader>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import VDatePickerEditableWithValidation from '@/components/validators/VDatePickerEditableWithValidation'
import { formatPhoneNumber } from '@/components/mixins/formatPhoneNumber'
import UserRole from '@/components/customer/UserRole'
import UserStatus from '@/components/customer/UserStatus'
import VAutocompleteWithValidation from '../../../components/validators/VAutocompleteWithValidation.vue'

export default {
  name: 'CustomerEdit',
  components: {
    VAutocompleteWithValidation,
    UserStatus,
    UserRole,
    VDatePickerEditableWithValidation,
  },
  mixins: [formatPhoneNumber],
  data() {
    return {
      skeleton: {
        article: 'heading, paragraph@2',
      },
      customer: {
        address: {},
        contactAddress: {},
      },
      customers: [],
      options: {
        page: 1,
        itemsPerPage: 10000,
        sortBy: ['createdAt'],
        sortDesc: [true],
      },
      genders: [],
      gendersLoading: false,
      customerLoading: false,
      customersLoading: false,
      saving: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.customer.gender) {
          this.customer.genderName = this.customer.gender.name
        }
      },
    },
  },
  created() {
    this.loadCustomerGenders()
    this.loadCustomer()
    this.loadCustomers()
  },
  methods: {
    ...mapActions({
      getCustomer: 'customer/getCustomer',
      searchCustomers: 'customer/searchCustomers',
      mergeCustomer: 'customer/mergeCustomer',
      listCustomerGender: 'enum/listCustomerGender',
    }),
    loadCustomerGenders() {
      this.gendersLoading = true
      this.listCustomerGender()
        .then((response) => {
          this.genders = response.data
        })
        .finally(() => {
          this.gendersLoading = false
        })
    },
    loadCustomer() {
      this.customerLoading = true
      this.getCustomer(this.$route.params.customerId)
        .then((response) => {
          this.customer = response.data
          if (this.customer.gender) {
            this.customer.genderName = this.customer.gender.name
          }
        })
        .finally(() => {
          this.customerLoading = false
        })
    },
    loadCustomers() {
      this.customersLoading = true
      const searchParams = {
        filterParams: this.$route.query,
        options: this.options,
      }
      this.searchCustomers(searchParams)
        .then((response) => {
          this.customers = response.data.items
          this.cancel = null
        })
        .finally(() => {
          this.customersLoading = false
        })
    },
    save() {
      let mergeCustomerIds = this.$route.query.ids.split(',')
      if (mergeCustomerIds.length === 0) {
        return
      }
      this.$confirm('', {
        title: 'Opravdu sloučit zákazníky?',
        buttonTrueText: 'Ano',
        buttonFalseText: 'Ne',
        icon: '',
        color: 'black',
      }).then((result) => {
        if (result) {
          this.saving = true
          this.mergeCustomer({
            customer: this.customer,
            mergeCustomerIds: mergeCustomerIds,
          })
            .then((response) => {
              if (response.status === 200 || response.status === 201) {
                this.$router.push({
                  name: 'customers.detail',
                  params: { customerId: this.customer.id },
                })
              }
            })
            .finally(() => {
              this.saving = false
            })
        }
      })
    },
  },
}
</script>
